import { Typography } from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import { useCpat } from "features/Cpat/useCpat"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useCallback, useEffect, useMemo } from "react"
import Joyride, { Step } from "react-joyride"
import { useLocation, useNavigate } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS, learningItemSlugs } from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../Context"
import { tourManager } from "../TourManager"
import { useTourContext } from "../TourProvider"
import BeforeYouStart from "./BeforeYouStart"
import { IMPACT_COURSE_TOUR_TARGETS } from "./constants"
import OnboardingStep from "./OnboardingStep"

const ImpactCourseTour = () => {
  const { isSPNYUser } = useSuraasaPlusContext()
  const { hasImpactCourse } = useEnrollments()
  const location = useLocation()
  const isLearningRoute = location.pathname === routes.learning
  const navigate = useNavigate()
  const { hasCompletedCpat } = useCpat()
  const isSmDown = useMediaQuery("(max-width: 599px)")
  const { spnyImpactCourseTour, isSpnyPlatformIntroCompleted } =
    useSuraasaPlusNewYear()

  const { step, setStep, stepIndex, setStepIndex, reset } = useTourContext()

  const onComplete = () => {
    reset()
    tourManager.completed(BROWSER_STORAGE_KEYS.spnyImpactCourse)
  }
  const updateStepIndex = useCallback(
    (index?: number) => {
      setTimeout(() => {
        if (index !== undefined) {
          setStepIndex(index)
        } else {
          setStepIndex(prev => (prev != undefined ? prev + 1 : 0))
        }
      }, 100)
    },
    [setStepIndex]
  )

  const steps = useMemo<Step[]>(
    () => [
      {
        target: `.${IMPACT_COURSE_TOUR_TARGETS.impactCourse}`,
        content: (
          <div>
            <Typography variant="title3" className="mb-1 text-start">
              Your Learning Space
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-2 text-start text-onSurface-600"
            >
              Continue your learning right where you left off. Your progress is
              saved automatically.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        disableOverlayClose: true,
      },
      {
        target: isSmDown
          ? `.${IMPACT_COURSE_TOUR_TARGETS.learningButtonMobile}`
          : `.${IMPACT_COURSE_TOUR_TARGETS.learningButtonWeb}`,
        content: (
          <div>
            <Typography variant="title3" className="mb-1 text-start">
              Access Your Course
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-2 text-start text-onSurface-600"
            >
              Click here to start your learning whenever you&apos;re ready.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        disableOverlayClose: true,
        placement: "top",
      },
      {
        target: `.${IMPACT_COURSE_TOUR_TARGETS.courseTour.milestoneList}`,
        content: (
          <div>
            <Typography variant="title3" className="mb-1 text-start">
              Access Your Learning Content
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-2 text-start text-onSurface-600"
            >
              Access your lessons, readings, and assessments, all grouped into
              milestones to guide your progress.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        disableOverlayClose: true,
      },
      {
        target: `.${IMPACT_COURSE_TOUR_TARGETS.courseTour.resourceTab}`,
        content: (
          <div>
            <Typography variant="title3" className="mb-1 text-start">
              Course Resources
            </Typography>
            <Typography
              variant="smallBody"
              className="mb-2 text-start text-onSurface-600"
            >
              Access supplementary articles, documents, and other helpful
              materials related to your course here.
            </Typography>
          </div>
        ),
        disableBeacon: true,
        disableOverlayClose: true,
      },
    ],
    [isSmDown]
  )

  const finalStepFunc = useCallback(() => {
    if (hasCompletedCpat) {
      onComplete()
    } else {
      setStep("warning")
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCompletedCpat])

  useEffect(() => {
    if (!isSPNYUser || !hasImpactCourse) {
      setStep(null)
      return
    }
    if (spnyImpactCourseTour === SpnyTourEnum.completed) {
      setStep(null)
      return
    }
    if (
      isLearningRoute &&
      !spnyImpactCourseTour &&
      isSpnyPlatformIntroCompleted
    ) {
      const timeoutId = setTimeout(() => {
        setStepIndex(0)
        setStep("joyride")
        tourManager.initiated(BROWSER_STORAGE_KEYS.spnyImpactCourse)
      }, 500)
      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLearningRoute,
    hasImpactCourse,
    isSPNYUser,
    isSpnyPlatformIntroCompleted,
  ])
  useEffect(() => {
    return () => {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (
    !isSPNYUser ||
    !hasImpactCourse ||
    spnyImpactCourseTour === SpnyTourEnum.completed
  )
    return null

  return (
    <>
      <Joyride
        steps={steps}
        run={step === "joyride"}
        stepIndex={stepIndex}
        tooltipComponent={props => (
          <OnboardingStep
            toolTipRenderProps={props}
            navigate={() => {
              navigate(`/course/${learningItemSlugs.impactCourse}/`)
            }}
            finalStepFunc={finalStepFunc}
            updateStepIndex={updateStepIndex}
          />
        )}
        scrollToFirstStep
        continuous
        disableOverlayClose
        disableScrollParentFix={isSmDown}
        disableCloseOnEsc
        disableScrolling={!isSmDown}
        scrollOffset={300}
        hideBackButton
        hideCloseButton
      />
      <BeforeYouStart open={step === "warning"} handleClose={onComplete} />
    </>
  )
}

export default ImpactCourseTour
